import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../../components/MainLayout';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import { Plus, Search, Pencil, Trash2, Users, AlertCircle } from 'lucide-react';
import api from '../../lib/api';
import type { Customer, CustomerListResponse } from '../../types/customer';
import { getCustomerDisplayName, formatAddress } from '../../types/customer';
import { CustomerDialog } from './CustomerDialog';
import { usePermissions } from '../../hooks/usePermissions';

export default function Customers() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('customers:create');
  const canEdit = hasPermission('customers:edit');
  const canDelete = hasPermission('customers:delete');

  // Fetch customers
  const { data, isLoading } = useQuery<CustomerListResponse>({
    queryKey: ['customers', page, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', '50');
      if (search) params.append('search', search);

      const response = await api.get(`/customers?${params.toString()}`);
      return response.data;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/customers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({
        title: 'Kunde gelöscht',
        description: 'Der Kunde wurde erfolgreich gelöscht.',
      });
    },
    onError: () => {
      toast({
        title: 'Fehler',
        description: 'Der Kunde konnte nicht gelöscht werden.',
        variant: 'destructive',
      });
    },
  });

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setDialogOpen(true);
  };

  const handleDelete = (customer: Customer) => {
    if (
      window.confirm(
        `Möchten Sie den Kunden "${getCustomerDisplayName(customer)}" wirklich löschen?`
      )
    ) {
      deleteMutation.mutate(customer.id);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Users className="h-7 w-7" />
              Kunden
            </h1>
            <p className="text-muted-foreground mt-1">
              Verwalten Sie Ihre Kunden und deren Daten
            </p>
          </div>
          {canCreate && (
            <Button
              onClick={() => {
                setEditingCustomer(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Neuer Kunde
            </Button>
          )}
        </div>

        {/* Search */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display text-lg">Suche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suchen nach Name, Kundennummer, E-Mail, Telefon..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display">Kunden{data ? ` (${data.total})` : ''}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 px-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : !data || data.customers.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Kunden gefunden</h3>
              <p className="text-muted-foreground mb-4">
                {search
                  ? 'Keine Kunden entsprechen Ihrer Suche.'
                  : 'Fügen Sie Ihren ersten Kunden hinzu.'}
              </p>
              {!search && canCreate && (
                <Button
                  onClick={() => {
                    setEditingCustomer(null);
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Ersten Kunden hinzufügen
                </Button>
              )}
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Kunde
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Kontakt
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Adresse
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Führerschein
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.customers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {getCustomerDisplayName(customer)}
                            {customer.is_blacklisted && (
                              <AlertCircle className="h-4 w-4 text-destructive" />
                            )}
                          </div>
                          {customer.customer_number && (
                            <div className="text-sm text-muted-foreground">
                              {customer.customer_number}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {customer.email && (
                            <div className="text-muted-foreground">{customer.email}</div>
                          )}
                          {customer.phone && <div>{customer.phone}</div>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm">{formatAddress(customer)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {customer.license_number ? (
                            <>
                              <div>{customer.license_number}</div>
                              {customer.license_class && (
                                <div className="text-muted-foreground">
                                  Klasse {customer.license_class}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {customer.is_blacklisted && (
                            <span className="px-2 py-1 rounded-pill text-xs font-medium bg-red-500/15 text-red-600 dark:text-red-400">
                              Blacklist
                            </span>
                          )}
                          {!customer.is_active && (
                            <span className="px-2 py-1 rounded-pill text-xs font-medium bg-muted text-muted-foreground">
                              Inaktiv
                            </span>
                          )}
                          {customer.is_active && !customer.is_blacklisted && (
                            <span className="px-2 py-1 rounded-pill text-xs font-medium bg-green-500/15 text-green-600 dark:text-green-400">
                              Aktiv
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(customer)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(customer)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {data.total_pages > 1 && (
                <div className="px-4 py-3 border-t flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Seite {data.page} von {data.total_pages} • {data.total}{' '}
                    Kunden gesamt
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Zurück
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === data.total_pages}
                      onClick={() => setPage(page + 1)}
                    >
                      Weiter
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <CustomerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customer={editingCustomer}
        onSuccess={() => {
          setDialogOpen(false);
          setEditingCustomer(null);
          queryClient.invalidateQueries({ queryKey: ['customers'] });
        }}
      />
    </MainLayout>
  );
}
